import { parseInstant, parseScheduleFields, parseOccurrenceTimingWrite, OCCURRENCE_TIMING_WRITE_FIELDS } from '@liquid-spirit/date-time/validation';
import { isSessionRequestDate, isSessionRequestPerson } from './session-requests.js';
import {readOccurrenceDisplayTimeZone} from './occurrence-read-contract.js';

export class ActivityRequestContractError extends Error {
  constructor(operation, detail) {
    super(`Invalid ${operation} request: ${detail}`);
    this.name = 'ActivityRequestContractError';
    this.code = 'INVALID_API_REQUEST';
  }
}

const isRecord = value => value !== null && typeof value === 'object' && !Array.isArray(value);
const isText = value => typeof value === 'string' && value.trim().length > 0;
const isObjectId = value => typeof value === 'string' && /^[a-f0-9]{24}$/i.test(value);
const optionalText = value => value == null || typeof value === 'string';
const frequencies = new Set(['Weekly', 'Bi-Weekly', 'Monthly', 'One-Off']);
const statuses = new Set(['Active', 'Complete', 'Cancelled']);
const generationControls = ['sessionGeneration', 'generationTemplateSession', 'generationFence'];
const writeFields = new Set(['activityType', 'title', 'description', 'address', 'imageUrl', 'status', 'groupDetails', 'contact', 'participantLimit', 'facilitatorLimit', 'grade', 'details', 'localSchedule']);

export function readActivityIdentifier(value) {
  if (!isObjectId(value)) throw new ActivityRequestContractError('identifier', 'expected an ObjectId.');
  return value;
}

export function readActivityDetailQuery(query = {}) {
  if (!isRecord(query) || query.sessionScope != null && typeof query.sessionScope !== 'string') {
    throw new ActivityRequestContractError('detail', 'sessionScope must be text.');
  }
  try { readOccurrenceDisplayTimeZone(query); }
  catch (error) { throw new ActivityRequestContractError('detail', error.message); }
  return query;
}

export function readActivityVenueQuery(query = {}) {
  if (!isRecord(query) || ['type', 'q'].some(field => query[field] != null && typeof query[field] !== 'string')
    || query.excludeAssigned != null && query.excludeAssigned !== '' && typeof query.excludeAssigned !== 'boolean'
      && (typeof query.excludeAssigned !== 'string' || !/^(true|false)$/i.test(query.excludeAssigned))) {
    throw new ActivityRequestContractError('venue lookup', 'invalid search fields.');
  }
  if (query.q?.trim()) {
    try { new RegExp(query.q.trim(), 'i'); } catch {
      throw new ActivityRequestContractError('venue lookup', 'invalid search expression.');
    }
  }
  return query;
}

/** Validate the stable fields of the main backend's activity create/update bodies. */
export function readActivityWriteRequest(payload, operation = 'update') {
  if (!isRecord(payload)) {
    throw new ActivityRequestContractError(operation, 'expected an object.');
  }
  try { if (payload.bookingEndsAt != null) parseInstant(payload.bookingEndsAt); }
  catch { throw new ActivityRequestContractError(operation, 'invalid booking finish timestamp.'); }
  try {
    if (generationControls.some(key => Object.hasOwn(payload, key))) throw new Error('session generation is operator-controlled');
    if (Object.hasOwn(payload, 'localSchedule')) parseScheduleFields(payload.localSchedule);
    if (Object.hasOwn(payload, 'initialSessionTiming')) {
      if (operation !== 'create' || !isRecord(payload.initialSessionTiming) || Object.keys(payload.initialSessionTiming).some(key => !OCCURRENCE_TIMING_WRITE_FIELDS.includes(key))) throw new Error('invalid initialSessionTiming');
      parseOccurrenceTimingWrite(payload.initialSessionTiming);
    }
    if (Object.hasOwn(payload, 'scheduleRevision') || Object.hasOwn(payload, 'expectedScheduleRevision') && (!Number.isSafeInteger(payload.expectedScheduleRevision) || payload.expectedScheduleRevision < 0)) throw new Error('invalid or server-owned schedule revision');
  } catch (error) { throw new ActivityRequestContractError(operation, error.message); }
  if (operation === 'create' && (!isText(payload.title) || !isText(payload.activityType)
    || !isObjectId(payload.community) || !isObjectId(payload.createdBy) || !isSessionRequestDate(payload.sessionDate)
    || !isRecord(payload.groupDetails) || !isText(payload.groupDetails.day)
    || !isText(payload.groupDetails.time) || !isText(payload.groupDetails.frequency))) {
    throw new ActivityRequestContractError(operation, 'missing or invalid activity and initial session fields.');
  }
  if (operation === 'update' && !Object.keys(payload).some(field => writeFields.has(field))) {
    throw new ActivityRequestContractError(operation, 'expected at least one writable field.');
  }
  if ((payload.title !== undefined && !isText(payload.title))
    || (payload.sessionDate !== undefined && !isSessionRequestDate(payload.sessionDate))
    || (payload.status !== undefined && !statuses.has(payload.status))
    || ['participantLimit', 'facilitatorLimit'].some(field => payload[field] !== undefined
      && (!Number.isInteger(payload[field]) || payload[field] < 0))) {
    throw new ActivityRequestContractError(operation, 'invalid title, date, status or limit.');
  }
  if ((payload.activityType !== undefined && !isText(payload.activityType))
    || !optionalText(payload.description) || payload.description?.length > 1000
    || !optionalText(payload.imageUrl) || !optionalText(payload.onlineLink)
    || (payload.grade != null && typeof payload.grade !== 'string' && !Number.isSafeInteger(payload.grade))
    || (payload.curriculumLesson != null && typeof payload.curriculumLesson !== 'string' && !Number.isSafeInteger(payload.curriculumLesson))
    || (payload.address != null && (!isRecord(payload.address)
      || ['streetAddress', 'suburb', 'city', 'state', 'postalCode'].some(field => !optionalText(payload.address[field]))))
    || (payload.groupDetails !== undefined && (!isRecord(payload.groupDetails)
      || ['day', 'time'].some(field => payload.groupDetails[field] !== undefined && !isText(payload.groupDetails[field]))
      || (payload.groupDetails.frequency !== undefined && !frequencies.has(payload.groupDetails.frequency))))
    || ['details', 'initialSessionDetails'].some(field => payload[field] != null && !isRecord(payload[field]))) {
    throw new ActivityRequestContractError(operation, 'invalid activity type, text, address, schedule or detail fields.');
  }
  if (operation === 'create' && ((payload.hostHousehold != null && !isObjectId(payload.hostHousehold))
    || (payload.isOpenExternally !== undefined && typeof payload.isOpenExternally !== 'boolean')
    || (payload.venues != null && (!Array.isArray(payload.venues) || payload.venues.some(venue => !isObjectId(venue))))
    || ['participants', 'facilitators'].some(field => payload[field] != null
      && (!Array.isArray(payload[field]) || payload[field].some(person => !isSessionRequestPerson(person)))))) {
    throw new ActivityRequestContractError(operation, 'invalid initial-session people, venues or activity references.');
  }
  return payload;
}
